import { useCallback, useEffect, useState } from 'react';
import type { CanonicalAssetGroupDto, CatalogResponseDto } from '@primetime/shared/apiContracts';
import { api, ApiError } from '../../services/apiClient';

export type CanonicalCatalogStatus = 'loading' | 'ready' | 'unavailable' | 'paywalled';

export function useCanonicalCatalog(endpoint = '/api/catalog') {
  const [assetGroups, setAssetGroups] = useState<CanonicalAssetGroupDto[]>([]);
  const [status, setStatus] = useState<CanonicalCatalogStatus>('loading');
  const [requestVersion, setRequestVersion] = useState(0);

  const retry = useCallback(() => {
    setRequestVersion((current) => current + 1);
  }, []);

  useEffect(() => {
    let active = true;
    setAssetGroups([]);
    setStatus('loading');

    api<CatalogResponseDto>(endpoint)
      .then(({ assetGroups: nextAssetGroups }) => {
        if (!active) return;
        setAssetGroups(Array.isArray(nextAssetGroups) ? nextAssetGroups : []);
        setStatus('ready');
      })
      .catch((error: unknown) => {
        if (!active) return;
        setAssetGroups([]);
        setStatus(error instanceof ApiError && [401, 403].includes(error.status) ? 'paywalled' : 'unavailable');
      });

    return () => {
      active = false;
    };
  }, [endpoint, requestVersion]);

  return {
    assetGroups,
    status,
    retry,
    isLoading: status === 'loading',
    isEmpty: status === 'ready' && assetGroups.length === 0,
    isUnavailable: status === 'unavailable'
  };
}
